// src/pages/InventoryReportPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PrintIcon from "@mui/icons-material/Print";
import DownloadIcon from "@mui/icons-material/Download";

import api from "../api";

const unwrapList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
};

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const formatNum = (v) => new Intl.NumberFormat("id-ID").format(num(v));

// ✅ kategori dari backend: pakai "category" (sesuai controller), fallback "category_name"
const getCategory = (r) => r?.category ?? r?.category_name ?? "-";

// escape HTML biar aman saat inject ke dokumen print
const esc = (val) =>
  String(val ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const formatDateID = (yyyyMmDd) => {
  if (!yyyyMmDd) return "-";
  const d = new Date(yyyyMmDd);
  if (Number.isNaN(d.getTime())) return String(yyyyMmDd);
  return d.toLocaleDateString("id-ID", { day: "2-digit", month: "2-digit", year: "numeric" });
};

export default function InventoryReportPage() {
  const [reportType, setReportType] = useState("detail"); // detail | outgoing

  const [from, setFrom] = useState(""); // YYYY-MM-DD
  const [to, setTo] = useState(""); // YYYY-MM-DD
  const [workAreaId, setWorkAreaId] = useState("");

  const [workAreas, setWorkAreas] = useState([]);
  const [rows, setRows] = useState([]);

  const [loading, setLoading] = useState(false);

  // ✅ fixed admin name
  const [adminNameInput, setAdminNameInput] = useState("Devi Andriani");

  const [snack, setSnack] = useState({
    open: false,
    msg: "",
    sev: "success",
  });

  const closeSnack = () => setSnack((s) => ({ ...s, open: false }));

  // ambil work area untuk laporan keluar
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/reports/inventory/work-areas");
        setWorkAreas(unwrapList(res.data));
      } catch (e) {
        setWorkAreas([]);
      }
    })();
  }, []);

  const canPrint = rows.length > 0;
  const canDownload = rows.length > 0;

  const totals = useMemo(() => {
    if (reportType === "detail") {
      return rows.reduce(
        (acc, r) => {
          acc.awal += num(r.stok_awal);
          acc.masuk += num(r.stok_masuk);
          acc.keluar += num(r.stok_keluar);
          acc.akhir += num(r.jumlah_stok ?? r.stok_akhir);
          return acc;
        },
        { awal: 0, masuk: 0, keluar: 0, akhir: 0 }
      );
    }

    // outgoing
    return rows.reduce(
      (acc, r) => {
        acc.keluar += num(r.jumlah ?? r.qty ?? r.stok_keluar);
        return acc;
      },
      { awal: 0, masuk: 0, keluar: 0, akhir: 0 }
    );
  }, [rows, reportType]);

  const handleOpen = async () => {
    setLoading(true);
    try {
      if (reportType === "detail") {
        const res = await api.get("/reports/inventory/detail", {
          params: {
            ...(from ? { from } : {}),
            ...(to ? { to } : {}),
          },
        });

        const list = unwrapList(res.data);
        setRows(list);

        setSnack({
          open: true,
          msg: `Berhasil memuat ${list.length} baris.`,
          sev: "success",
        });
      } else {
        if (!from || !to) {
          setSnack({
            open: true,
            msg: "Tanggal dari & sampai wajib diisi.",
            sev: "error",
          });
          setLoading(false);
          return;
        }

        const res = await api.get("/reports/inventory/outgoing", {
          params: {
            ...(workAreaId ? { work_area_id: Number(workAreaId) } : {}),
            from,
            to,
          },
        });

        const list = unwrapList(res.data);
        setRows(list);

        setSnack({
          open: true,
          msg: `Berhasil memuat ${list.length} baris.`,
          sev: "success",
        });
      }
    } catch (e) {
      console.error("Laporan gagal:", e);
      setRows([]);
      setSnack({
        open: true,
        msg: "Gagal memuat laporan. Cek Network (status/response).",
        sev: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const buildCsv = () => {
    if (reportType === "detail") {
      const headers = ["ID", "Nama Produk", "Kategori", "Stok Awal", "Stok Masuk", "Stok Keluar", "Jumlah Stok"];
      const lines = rows.map((r) => [
        r.id,
        (r.name ?? "").toString().replaceAll(",", " "),
        getCategory(r).toString().replaceAll(",", " "),
        num(r.stok_awal),
        num(r.stok_masuk),
        num(r.stok_keluar),
        num(r.jumlah_stok ?? r.stok_akhir),
      ]);
      return [headers, ...lines].map((x) => x.join(",")).join("\n");
    }

    const headers = ["No", "Nomor pengajuan", "Nama Unit (Nama Wilker/Timker)", "Tanggal Pengajuan", "Barang yang diajukan", "Jumlah"];
    const lines = rows.map((r, i) => [
      i + 1,
      (r.number ?? "-").toString().replaceAll(",", " "),
      (r.unit_name ?? "-").toString().replaceAll(",", " "),
      (r.tanggal_pengajuan ?? "-").toString().replaceAll(",", " "),
      (r.barang ?? "-").toString().replaceAll(",", " "),
      num(r.jumlah ?? r.qty ?? 0),
    ]);

    return [headers, ...lines].map((x) => x.join(",")).join("\n");
  };

  const handleDownloadCsv = () => {
    const csv = buildCsv();
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = reportType === "detail" ? "laporan-rincian-persediaan.csv" : "laporan-keluar-barang.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    const now = new Date();
    const tanggal = now.toLocaleDateString("id-ID", { day: "2-digit", month: "2-digit", year: "numeric" });
    const jam = now.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" });

    const periodeText = `${formatDateID(from)} s/d ${formatDateID(to)}`;
    const signer = (adminNameInput || "Devi Andriani").toString().trim() || "Devi Andriani";

    // ✅ file ttd di build/public: /ttd-devi.(png/jpg/webp)
    const sigPng = `${window.location.origin}/ttd-devi.png`;
    const sigJpg = `${window.location.origin}/ttd-devi.jpg`;
    const sigWebp = `${window.location.origin}/ttd-devi.webp`;

    const makeDetailRows = () =>
      rows
        .map(
          (r, i) => `
          <tr>
            <td>${i + 1}</td>
            <td>${esc(r.id)}</td>
            <td>${esc(r.name ?? "-")}</td>
            <td>${esc(getCategory(r))}</td>
            <td style="text-align:right">${esc(num(r.stok_awal))}</td>
            <td style="text-align:right">${esc(num(r.stok_masuk))}</td>
            <td style="text-align:right">${esc(num(r.stok_keluar))}</td>
            <td style="text-align:right">${esc(num(r.jumlah_stok ?? r.stok_akhir))}</td>
          </tr>
        `
        )
        .join("");

    const makeOutgoingRows = () =>
      rows
        .map(
          (r, i) => `
          <tr>
            <td>${i + 1}</td>
            <td>${esc(r.number ?? "-")}</td>
            <td>${esc(r.unit_name ?? "-")}</td>
            <td>${esc(r.tanggal_pengajuan ?? "-")}</td>
            <td>${esc(r.barang ?? "-")}</td>
            <td style="text-align:right">${esc(num(r.jumlah ?? r.qty ?? 0))}</td>
          </tr>
        `
        )
        .join("");

    const html = `
      <!doctype html>
      <html>
      <head>
        <meta charset="utf-8" />
        <title>Cetak Laporan</title>
        <style>
          body { font-family: Arial, sans-serif; font-size: 11pt; color:#000; }
          h1 { font-size: 16pt; margin: 0; }
          .meta { font-size: 10pt; margin-top: 4px; }
          .meta-row { margin-top: 2px; }

          table { width:100%; border-collapse: collapse; margin-top: 14px; }
          th, td { border: 1px solid #000; padding: 6px; vertical-align: top; }
          th { background: #f1f1f1; text-align:left; }

          @page { size: A4; margin: 10mm; }
          tr { page-break-inside: avoid; }

          .footer { margin-top: 28px; display:flex; justify-content:flex-end; }
          .sign { width: 280px; text-align:center; }
          .sign .role { margin-top: 8px; }
          .sign img { display:block; margin: 10px auto 0; max-width: 230px; max-height: 90px; object-fit: contain; }
          .sign .name { margin-top: 6px; font-weight: 700; }
        </style>
      </head>
      <body>
        <h1>${reportType === "detail" ? "Laporan Rincian Persediaan" : "Laporan Keluar Barang"}</h1>
        <div class="meta">
          <div class="meta-row">${esc(tanggal)} ${esc(jam)}</div>
          <div class="meta-row"><b>Periode:</b> ${esc(periodeText)}</div>
        </div>

        <table>
          <thead>
            ${
              reportType === "detail"
                ? `<tr>
                    <th style="width:40px">No</th>
                    <th style="width:60px">ID</th>
                    <th>Nama Produk</th>
                    <th>Kategori</th>
                    <th style="width:90px">Stok Awal</th>
                    <th style="width:90px">Stok Masuk</th>
                    <th style="width:90px">Stok Keluar</th>
                    <th style="width:110px">Jumlah Stok</th>
                  </tr>`
                : `<tr>
                    <th style="width:40px">No</th>
                    <th style="width:140px">Nomor pengajuan</th>
                    <th style="width:220px">Nama Unit (Wilker/Timker)</th>
                    <th style="width:120px">Tanggal Pengajuan</th>
                    <th>Barang yang diajukan</th>
                    <th style="width:90px;text-align:right">Jumlah</th>
                  </tr>`
            }
          </thead>
          <tbody>
            ${reportType === "detail" ? makeDetailRows() : makeOutgoingRows()}
          </tbody>
        </table>

        <div class="footer">
          <div class="sign">
            <div>${esc(tanggal)}</div>
            <div class="role">Petugas/Admin</div>

            <!-- ✅ TTD: coba PNG -> JPG -> WEBP, kalau semua gagal disembunyikan -->
            <img
              src="${esc(sigPng)}"
              alt="TTD"
              onerror="if(!this.dataset.try){this.dataset.try='jpg'; this.src='${esc(sigJpg)}';}
                       else if(this.dataset.try==='jpg'){this.dataset.try='webp'; this.src='${esc(sigWebp)}';}
                       else {this.style.display='none';}"
            />

            <div class="name">(${esc(signer)})</div>
          </div>
        </div>
      </body>
      </html>
    `;

    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    document.body.appendChild(iframe);

    const win = iframe.contentWindow;
    const doc = win?.document;
    if (!doc) {
      window.print();
      return;
    }

    doc.open();
    doc.write(html);
    doc.close();

    setTimeout(() => {
      win.focus();
      win.print();
      setTimeout(() => document.body.removeChild(iframe), 1000);
    }, 350);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" sx={{ mb: 1 }}>
        Laporan Persediaan
      </Typography>
      <Divider sx={{ mb: 2 }} />

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <InputLabel>Pilih Laporan</InputLabel>
            <Select
              label="Pilih Laporan"
              value={reportType}
              onChange={(e) => {
                setReportType(e.target.value);
                setRows([]);
                setWorkAreaId("");
              }}
            >
              <MenuItem value="detail">Laporan Rincian Persediaan</MenuItem>
              <MenuItem value="outgoing">Laporan Keluar Barang</MenuItem>
            </Select>
          </FormControl>

          {reportType === "outgoing" && (
            <FormControl size="small" sx={{ minWidth: 260 }}>
              <InputLabel>Wilker/Timker</InputLabel>
              <Select label="Wilker/Timker" value={workAreaId} onChange={(e) => setWorkAreaId(e.target.value)}>
                <MenuItem value="">
                  <em>Semua</em>
                </MenuItem>
                {workAreas.map((w) => (
                  <MenuItem key={w.id} value={String(w.id)}>
                    {w.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <TextField
            size="small"
            label="Dari"
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            size="small"
            label="Sampai"
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          {/* Opsional: kalau mau dikunci, set disabled */}
          <TextField
            size="small"
            label="Nama Admin"
            value={adminNameInput}
            onChange={(e) => setAdminNameInput(e.target.value)}
            sx={{ minWidth: 220 }}
          />

          <Button variant="contained" startIcon={<SearchIcon />} onClick={handleOpen} disabled={loading}>
            Buka
          </Button>

          <Button variant="outlined" startIcon={<PrintIcon />} onClick={handlePrint} disabled={!canPrint}>
            Cetak
          </Button>

          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadCsv} disabled={!canDownload}>
            Download CSV
          </Button>
        </Box>

        <Box sx={{ mt: 1.5, display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Chip label={`Total Awal: ${formatNum(totals.awal)}`} />
          <Chip label={`Masuk: ${formatNum(totals.masuk)}`} />
          <Chip label={`Keluar: ${formatNum(totals.keluar)}`} />
          <Chip label={`Akhir: ${formatNum(totals.akhir)}`} />
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ borderRadius: 2 }}>
        <Box sx={{ maxHeight: 560, overflowY: "auto", overflowX: "auto" }}>
          <TableContainer>
            <Table stickyHeader sx={{ minWidth: 1100 }}>
              <TableHead>
                {reportType === "detail" ? (
                  <TableRow>
                    <TableCell width={60}>ID</TableCell>
                    <TableCell>Nama Produk</TableCell>
                    <TableCell width={220}>Kategori</TableCell>
                    <TableCell align="right" width={110}>Stok Awal</TableCell>
                    <TableCell align="right" width={110}>Stok Masuk</TableCell>
                    <TableCell align="right" width={110}>Stok Keluar</TableCell>
                    <TableCell align="right" width={130}>Jumlah Stok</TableCell>
                  </TableRow>
                ) : (
                  <TableRow>
                    <TableCell width={60}>No</TableCell>
                    <TableCell width={170}>Nomor pengajuan</TableCell>
                    <TableCell width={260}>Nama Unit (Wilker/Timker)</TableCell>
                    <TableCell width={140}>Tanggal Pengajuan</TableCell>
                    <TableCell>Barang yang diajukan</TableCell>
                    <TableCell align="right" width={110}>Jumlah</TableCell>
                  </TableRow>
                )}
              </TableHead>

              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={reportType === "detail" ? 7 : 6} align="center" sx={{ py: 4 }}>
                      Belum ada data.
                    </TableCell>
                  </TableRow>
                ) : reportType === "detail" ? (
                  rows.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell>{r.id}</TableCell>
                      <TableCell>{r.name}</TableCell>
                      <TableCell>{getCategory(r)}</TableCell>
                      <TableCell align="right">{formatNum(r.stok_awal)}</TableCell>
                      <TableCell align="right">{formatNum(r.stok_masuk)}</TableCell>
                      <TableCell align="right">{formatNum(r.stok_keluar)}</TableCell>
                      <TableCell align="right">{formatNum(r.jumlah_stok ?? r.stok_akhir)}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  rows.map((r, idx) => (
                    <TableRow key={`${r.number ?? idx}-${idx}`}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{r.number ?? "-"}</TableCell>
                      <TableCell>{r.unit_name ?? "-"}</TableCell>
                      <TableCell>{r.tanggal_pengajuan ?? "-"}</TableCell>
                      <TableCell>{r.barang ?? "-"}</TableCell>
                      <TableCell align="right">{formatNum(r.jumlah ?? r.qty ?? 0)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Paper>

      <Snackbar open={snack.open} autoHideDuration={4500} onClose={closeSnack}>
        <Alert onClose={closeSnack} severity={snack.sev} sx={{ width: "100%" }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Container>
  );
}
